import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/adminApi";
import { Badge, EmptyState, inputCls, ENQUIRY_STATUSES, STATUS_LABELS } from "./shared";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const statusLabel = (s) => STATUS_LABELS[s] || s.charAt(0).toUpperCase() + s.slice(1);

export default function AdminEnquiries() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [notesFor, setNotesFor] = useState(null);
    const [notes, setNotes] = useState("");

    const load = () =>
        api.get("/enquiries", { params: { q, status, page } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load enquiries"));
    useEffect(() => { load(); }, [q, status, page]);

    const setEnquiryStatus = async (item, newStatus) => {
        try {
            await api.patch(`/enquiries/${item.source}/${item.id}`, { status: newStatus });
            toast.success(`Marked as ${statusLabel(newStatus)}`);
            load();
        } catch {
            toast.error("Could not update status");
        }
    };

    const saveNotes = async () => {
        try {
            await api.patch(`/enquiries/${notesFor.source}/${notesFor.id}`, { notes });
            toast.success("Notes saved");
            setNotesFor(null);
            load();
        } catch {
            toast.error("Could not save notes");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-enquiries">
            <div>
                <h1 className="font-serif text-3xl font-semibold text-ink">Enquiries</h1>
                <p className="mt-1 text-sm text-muted-foreground">Website enquiries and free-trial requests land here automatically</p>
            </div>

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-60">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name or email…" className={`${inputCls} pl-11`} data-testid="enquiries-search-input" />
                </div>
                <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger className={`${inputCls} w-52`} data-testid="enquiries-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {ENQUIRY_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {!data || data.items.length === 0 ? (
                <EmptyState message="No enquiries match. New website enquiries will appear here automatically." />
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-white card-shadow">
                    <table className="w-full text-sm" data-testid="enquiries-table">
                        <thead>
                            <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                <th className="px-5 py-4 font-medium">From</th>
                                <th className="px-5 py-4 font-medium">Type</th>
                                <th className="px-5 py-4 font-medium">Subject</th>
                                <th className="px-5 py-4 font-medium">Message</th>
                                <th className="px-5 py-4 font-medium">Date</th>
                                <th className="px-5 py-4 font-medium">Status</th>
                                <th className="px-5 py-4 font-medium">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((e) => (
                                <tr key={`${e.source}-${e.id}`} className="border-b border-border/60 last:border-0" data-testid={`enquiry-row-${e.id}`}>
                                    <td className="px-5 py-4">
                                        <p className="font-medium text-ink">{e.display_name}</p>
                                        <p className="text-xs text-muted-foreground">{e.email}</p>
                                        {e.phone && <p className="text-xs text-muted-foreground">{e.phone}</p>}
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${e.source === "trial" ? "bg-violet-100 text-violet-700" : "bg-saffron-soft text-saffron-dark"}`}>
                                            {e.source === "trial" ? "Free Trial" : "Enquiry"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4">{e.subject || "—"}</td>
                                    <td className="max-w-56 px-5 py-4"><p className="line-clamp-2 text-xs text-muted-foreground">{e.message}</p></td>
                                    <td className="px-5 py-4 text-xs text-muted-foreground">{(e.created_at || "").slice(0, 10)}</td>
                                    <td className="px-5 py-4">
                                        <Select value={e.status || "new"} onValueChange={(v) => setEnquiryStatus(e, v)}>
                                            <SelectTrigger className="h-9 w-44 bg-white text-xs" data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {ENQUIRY_STATUSES.map((s) => (
                                                    <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-5 py-4">
                                        <button onClick={() => { setNotesFor(e); setNotes(e.notes || ""); }} data-testid={`enquiry-notes-${e.id}`}
                                            className="rounded-full border border-border px-4 py-1.5 text-xs font-medium text-ink hover:bg-sand">
                                            {e.notes ? "View notes" : "Add notes"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data && data.pages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-border px-5 py-2 text-sm disabled:opacity-40">Previous</button>
                    <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
                    <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-full border border-border px-5 py-2 text-sm disabled:opacity-40">Next</button>
                </div>
            )}

            <Dialog open={!!notesFor} onOpenChange={(o) => !o && setNotesFor(null)}>
                <DialogContent data-testid="enquiry-notes-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Notes — {notesFor?.display_name}</DialogTitle>
                    </DialogHeader>
                    <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls}
                        placeholder="Follow-up details, preferred timing, parent feedback…" data-testid="enquiry-notes-textarea" />
                    <div className="flex justify-end gap-3">
                        <button onClick={() => setNotesFor(null)} className="rounded-full border border-border px-6 py-2.5 text-sm font-medium">Cancel</button>
                        <button onClick={saveNotes} className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal" data-testid="enquiry-notes-save">Save Notes</button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
